/**
 * WebSocket Hooks
 * Event Master v1.5 - Backend Integration
 * День 10: WebSocket Module (5 endpoints)
 * 
 * React хуки для работы с WebSocket API
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getWebSocketConnections,
  getWebSocketConnection,
  connectWebSocket,
  disconnectWebSocket,
  getWebSocketChannels,
  getWebSocketChannel,
  subscribeToChannel,
  unsubscribeFromChannel,
  getWebSocketMessages,
  sendWebSocketMessage,
  getRealTimeNotifications,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  getChatMessages,
  sendChatMessage,
  getRealTimeEvents,
  subscribeToEvents,
  unsubscribeFromEvents,
  getRealTimeAnalytics,
  subscribeToAnalytics,
  unsubscribeFromAnalytics,
  getWebSocketStatus,
  getWebSocketStats,
  WebSocketConnection,
  WebSocketMessage,
  WebSocketChannel
} from '../services/api/websocket.api';
import { useSnackbar } from './useSnackbar';

// ===== ХУКИ ДЛЯ WEB SOCKET СОЕДИНЕНИЙ =====

/**
 * Хук для получения WebSocket соединений
 */
export const useWebSocketConnections = () => {
  const [connections, setConnections] = useState<WebSocketConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketConnections();
      setConnections(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке WebSocket соединений';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  return {
    connections,
    loading,
    error,
    refetch: fetchConnections
  };
};

/**
 * Хук для получения одного WebSocket соединения
 */
export const useWebSocketConnection = (connectionId: string) => {
  const [connection, setConnection] = useState<WebSocketConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchConnection = useCallback(async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketConnection(connectionId);
      setConnection(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке WebSocket соединения';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [connectionId, showSnackbar]);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  return {
    connection,
    loading,
    error,
    refetch: fetchConnection
  };
};

/**
 * Хук для подключения к WebSocket
 */
export const useConnectWebSocket = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const connect = useCallback(async (channels?: string[]) => {
    try {
      setLoading(true);
      const data = await connectWebSocket(channels);
      showSnackbar('WebSocket соединение установлено', 'success');
      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при подключении к WebSocket';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    connect,
    loading
  };
};

/**
 * Хук для отключения от WebSocket
 */
export const useDisconnectWebSocket = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const disconnect = useCallback(async (connectionId: string) => {
    try {
      setLoading(true);
      await disconnectWebSocket(connectionId);
      showSnackbar('WebSocket соединение разорвано', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отключении от WebSocket';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    disconnect,
    loading
  };
};

// ===== ХУКИ ДЛЯ КАНАЛОВ =====

/**
 * Хук для получения WebSocket каналов
 */
export const useWebSocketChannels = () => {
  const [channels, setChannels] = useState<WebSocketChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchChannels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketChannels();
      setChannels(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке WebSocket каналов';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  return {
    channels,
    loading,
    error,
    refetch: fetchChannels
  };
};

/**
 * Хук для получения одного WebSocket канала
 */
export const useWebSocketChannel = (channelId: string) => {
  const [channel, setChannel] = useState<WebSocketChannel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchChannel = useCallback(async () => {
    if (!channelId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketChannel(channelId);
      setChannel(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке WebSocket канала';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [channelId, showSnackbar]);

  useEffect(() => {
    fetchChannel();
  }, [fetchChannel]);

  return {
    channel,
    loading,
    error,
    refetch: fetchChannel
  };
};

/**
 * Хук для подписки на канал
 */
export const useSubscribeToChannel = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const subscribe = useCallback(async (channelId: string) => {
    try {
      setLoading(true);
      await subscribeToChannel(channelId);
      showSnackbar('Подписка на канал оформлена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при подписке на канал';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    subscribe,
    loading
  };
};

/**
 * Хук для отписки от канала
 */
export const useUnsubscribeFromChannel = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const unsubscribe = useCallback(async (channelId: string) => {
    try {
      setLoading(true);
      await unsubscribeFromChannel(channelId);
      showSnackbar('Отписка от канала выполнена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отписке от канала';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    unsubscribe,
    loading
  };
};

// ===== ХУКИ ДЛЯ СООБЩЕНИЙ =====

/**
 * Хук для получения WebSocket сообщений
 */
export const useWebSocketMessages = (channelId?: string) => {
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchMessages = useCallback(async () => {
    if (!channelId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketMessages(channelId);
      setMessages(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке WebSocket сообщений';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [channelId, showSnackbar]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    loading,
    error,
    refetch: fetchMessages
  };
};

/**
 * Хук для отправки WebSocket сообщения
 */
export const useSendWebSocketMessage = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const sendMessage = useCallback(async (channelId: string, messageData: Partial<WebSocketMessage>) => {
    try {
      setLoading(true);
      await sendWebSocketMessage(channelId, messageData);
      showSnackbar('Сообщение отправлено', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отправке сообщения';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    sendMessage,
    loading
  };
};

// ===== ХУКИ ДЛЯ REAL-TIME УВЕДОМЛЕНИЙ =====

/**
 * Хук для получения real-time уведомлений
 */
export const useRealTimeNotifications = () => {
  const [notifications, setNotifications] = useState<WebSocketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRealTimeNotifications();
      setNotifications(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке real-time уведомлений';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications,
    loading,
    error,
    refetch: fetchNotifications
  };
};

/**
 * Хук для подписки на уведомления
 */
export const useSubscribeToNotifications = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const subscribe = useCallback(async () => {
    try {
      setLoading(true);
      await subscribeToNotifications();
      showSnackbar('Подписка на уведомления оформлена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при подписке на уведомления';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    subscribe,
    loading
  };
};

/**
 * Хук для отписки от уведомлений
 */
export const useUnsubscribeFromNotifications = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const unsubscribe = useCallback(async () => {
    try {
      setLoading(true);
      await unsubscribeFromNotifications();
      showSnackbar('Отписка от уведомлений выполнена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отписке от уведомлений';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    unsubscribe,
    loading
  };
};

// ===== ХУКИ ДЛЯ ЧАТА =====

/**
 * Хук для получения сообщений чата
 */
export const useChatMessages = (chatId?: string) => {
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getChatMessages(chatId);
      setMessages(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке сообщений чата';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [chatId, showSnackbar]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    loading,
    error,
    refetch: fetchMessages
  };
};

/**
 * Хук для отправки сообщения в чат
 */
export const useSendChatMessage = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const sendMessage = useCallback(async (chatId: string, messageData: Partial<WebSocketMessage>) => {
    try {
      setLoading(true);
      await sendChatMessage(chatId, messageData);
      showSnackbar('Сообщение в чат отправлено', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отправке сообщения в чат';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    sendMessage,
    loading
  };
};

// ===== ХУКИ ДЛЯ REAL-TIME СОБЫТИЙ =====

/**
 * Хук для получения real-time событий
 */
export const useRealTimeEvents = () => {
  const [events, setEvents] = useState<WebSocketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { showSnackbar } = useSnackbar();

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRealTimeEvents();
      setEvents(data);
      setIsConnected(true);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке real-time событий';
      setError(errorMessage);
      setIsConnected(false);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    isConnected,
    refetch: fetchEvents
  };
};

/**
 * Хук для подписки на события
 */
export const useSubscribeToEvents = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const subscribe = useCallback(async () => {
    try {
      setLoading(true);
      await subscribeToEvents();
      showSnackbar('Подписка на события оформлена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при подписке на события';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    subscribe,
    loading
  };
};

/**
 * Хук для отписки от событий
 */
export const useUnsubscribeFromEvents = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const unsubscribe = useCallback(async () => {
    try {
      setLoading(true);
      await unsubscribeFromEvents();
      showSnackbar('Отписка от событий выполнена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отписке от событий';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    unsubscribe,
    loading
  };
};

// ===== ХУКИ ДЛЯ REAL-TIME АНАЛИТИКИ =====

/**
 * Хук для получения real-time аналитики
 */
export const useRealTimeAnalytics = () => {
  const [analytics, setAnalytics] = useState<WebSocketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRealTimeAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке real-time аналитики';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    refetch: fetchAnalytics
  };
};

/**
 * Хук для подписки на аналитику
 */
export const useSubscribeToAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const subscribe = useCallback(async () => {
    try {
      setLoading(true);
      await subscribeToAnalytics();
      showSnackbar('Подписка на аналитику оформлена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при подписке на аналитику';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    subscribe,
    loading
  };
};

/**
 * Хук для отписки от аналитики
 */
export const useUnsubscribeFromAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const unsubscribe = useCallback(async () => {
    try {
      setLoading(true);
      await unsubscribeFromAnalytics();
      showSnackbar('Отписка от аналитики выполнена', 'success');
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при отписке от аналитики';
      showSnackbar(errorMessage, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return {
    unsubscribe,
    loading
  };
};

// ===== ХУКИ ДЛЯ СТАТУСА И СТАТИСТИКИ =====

/**
 * Хук для получения статуса WebSocket
 */
export const useWebSocketStatus = () => {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketStatus();
      setStatus(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке статуса WebSocket';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus
  };
};

/**
 * Хук для получения статистики WebSocket
 */
export const useWebSocketStats = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebSocketStats();
      setStats(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка при загрузке статистики WebSocket';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data: stats,
    stats,
    loading,
    error,
    refetch: fetchStats
  };
};
